// eslint-disable-next-line @typescript-eslint/triple-slash-reference -- ambient typings for markdown-it-mark
/// <reference path="./types.d.ts" />
/**
 * markdown-it configured for the Obsidian dialect Nexus imports: wikilinks and
 * embeds, #tags, ==highlight==, %%comments%%, callouts, task lists with Obsidian
 * Tasks status characters, GFM tables. Unknown constructs become raw tokens so
 * they survive a round trip unchanged.
 */
import MarkdownIt from 'markdown-it';
import type StateBlock from 'markdown-it/lib/rules_block/state_block.mjs';
import type StateCore from 'markdown-it/lib/rules_core/state_core.mjs';
import type StateInline from 'markdown-it/lib/rules_inline/state_inline.mjs';
import type Token from 'markdown-it/lib/token.mjs';
import markPlugin from 'markdown-it-mark';

/** `#tag`: letters, digits, `_`, `-`, `/`; must contain a non-digit (Obsidian rule). */
export const TAG_BODY = /^[\p{L}\p{N}_\-/]+/u;
export const isTag = (body: string) => /[^\d/]/u.test(body) && !body.endsWith('/');

export interface WikiTarget {
  target: string;
  heading: string | null;
  alias: string | null;
}

export function parseWikiTarget(inner: string): WikiTarget {
  const bar = inner.indexOf('|');
  let main = bar === -1 ? inner : inner.slice(0, bar);
  const alias = bar === -1 ? null : inner.slice(bar + 1);
  // Inside tables Obsidian escapes the pipe as `\|`.
  if (main.endsWith('\\')) main = main.slice(0, -1);
  const hash = main.indexOf('#');
  return {
    target: (hash === -1 ? main : main.slice(0, hash)).trim(),
    heading: hash === -1 ? null : main.slice(hash + 1).trim() || null,
    alias: alias?.trim() || null,
  };
}

function wikilinkRule(state: StateInline, silent: boolean): boolean {
  const src = state.src;
  let pos = state.pos;
  const embed = src.charCodeAt(pos) === 0x21; /* ! */
  if (embed) pos++;
  if (!src.startsWith('[[', pos)) return false;
  const end = src.indexOf(']]', pos + 2);
  if (end === -1) return false;
  const inner = src.slice(pos + 2, end);
  if (inner.includes('\n') || inner.includes('[[') || !inner.trim()) return false;
  if (!silent) {
    const tok = state.push(embed ? 'embed_inline' : 'wikilink', '', 0);
    tok.meta = parseWikiTarget(inner);
    tok.content = src.slice(state.pos, end + 2);
  }
  state.pos = end + 2;
  return true;
}

function hashtagRule(state: StateInline, silent: boolean): boolean {
  const pos = state.pos;
  if (state.src.charCodeAt(pos) !== 0x23 /* # */) return false;
  const prev = pos === 0 ? '' : state.src[pos - 1]!;
  if (prev && !/\s/u.test(prev)) return false;
  const m = TAG_BODY.exec(state.src.slice(pos + 1));
  if (!m || !isTag(m[0])) return false;
  if (!silent) {
    const tok = state.push('hashtag', '', 0);
    tok.meta = { tag: m[0] };
  }
  state.pos = pos + 1 + m[0].length;
  return true;
}

/** `%%comment%%` inside a paragraph. */
function inlineCommentRule(state: StateInline, silent: boolean): boolean {
  const pos = state.pos;
  if (!state.src.startsWith('%%', pos)) return false;
  const end = state.src.indexOf('%%', pos + 2);
  if (end === -1) return false;
  if (!silent) {
    const tok = state.push('raw_inline', '', 0);
    tok.content = state.src.slice(pos, end + 2);
  }
  state.pos = end + 2;
  return true;
}

function lineText(state: StateBlock, line: number): string {
  return state.src.slice(state.bMarks[line]! + state.tShift[line]!, state.eMarks[line]);
}

/** Emit the source lines [start, end) verbatim as a raw block. */
function pushRaw(state: StateBlock, start: number, end: number) {
  const tok = state.push('raw_block', '', 0);
  tok.content = state.getLines(start, end, state.blkIndent, false).replace(/\n$/, '');
  tok.map = [start, end];
  state.line = end;
}

/** Multi-line `%%` comment blocks. */
function blockCommentRule(state: StateBlock, start: number, end: number, silent: boolean) {
  if (state.sCount[start]! - state.blkIndent >= 4) return false;
  const first = lineText(state, start);
  if (!first.startsWith('%%')) return false;
  if (first.length > 2 && first.indexOf('%%', 2) !== -1) return false; // single-line: inline rule
  let line = start + 1;
  while (line < end && !lineText(state, line).includes('%%')) line++;
  if (line >= end) return false;
  if (silent) return true;
  pushRaw(state, start, line + 1);
  return true;
}

/** Footnote definitions `[^id]: …` (with indented continuation lines). */
function footnoteDefRule(state: StateBlock, start: number, end: number, silent: boolean) {
  if (state.sCount[start]! - state.blkIndent >= 4) return false;
  if (!/^\[\^[^\]\s]+\]:/.test(lineText(state, start))) return false;
  if (silent) return true;
  let line = start + 1;
  while (line < end && (state.isEmpty(line) ? false : state.sCount[line]! - state.blkIndent >= 4))
    line++;
  pushRaw(state, start, line);
  return true;
}

function findClose(tokens: Token[], openIdx: number): number {
  const open = tokens[openIdx]!;
  const closeType = open.type.replace(/_open$/, '_close');
  let depth = 0;
  for (let i = openIdx; i < tokens.length; i++) {
    const t = tokens[i]!;
    if (t.type === open.type && t.level === open.level) depth++;
    else if (t.type === closeType && t.level === open.level && --depth === 0) return i;
  }
  return -1;
}

const TASK_RE = /^\[(.)\](?: |$)/;

/** Turn list items starting with `[x] ` into task items; lists of only tasks into task lists. */
function taskListsRule(state: StateCore) {
  const tokens = state.tokens;
  for (let i = 0; i < tokens.length; i++) {
    const list = tokens[i]!;
    if (list.type !== 'bullet_list_open') continue;
    const close = findClose(tokens, i);
    const items: number[] = [];
    for (let j = i + 1; j < close; j++) {
      const t = tokens[j]!;
      if (t.type === 'list_item_open' && t.level === list.level + 1) items.push(j);
    }
    const allTasks = items.every((j) => {
      const inline = tokens[j + 2];
      return (
        tokens[j + 1]?.type === 'paragraph_open' &&
        inline?.type === 'inline' &&
        TASK_RE.test(inline.content)
      );
    });
    if (!allTasks || items.length === 0) continue;
    list.type = 'task_list_open';
    tokens[close]!.type = 'task_list_close';
    for (const j of items) {
      const inline = tokens[j + 2]!;
      const m = TASK_RE.exec(inline.content)!;
      inline.content = inline.content.slice(m[0].length);
      const itemClose = findClose(tokens, j);
      tokens[j]!.type = 'task_item_open';
      tokens[j]!.meta = { status: m[1] };
      tokens[itemClose]!.type = 'task_item_close';
    }
  }
}

const CALLOUT_RE = /^\[!([^\]\s]+)\]([+-]?)[ \t]*(.*)$/;

/** `> [!KIND]± Title` blockquotes become callouts. */
function calloutsRule(state: StateCore) {
  const tokens = state.tokens;
  for (let i = 0; i < tokens.length; i++) {
    const open = tokens[i]!;
    if (open.type !== 'blockquote_open') continue;
    const inline = tokens[i + 2];
    if (tokens[i + 1]?.type !== 'paragraph_open' || inline?.type !== 'inline') continue;
    const nl = inline.content.indexOf('\n');
    const first = nl === -1 ? inline.content : inline.content.slice(0, nl);
    const m = CALLOUT_RE.exec(first);
    if (!m) continue;
    const close = findClose(tokens, i);
    open.type = 'callout_open';
    open.meta = { kind: m[1], fold: m[2] || null, title: m[3]!.trim() };
    tokens[close]!.type = 'callout_close';
    if (nl === -1) {
      tokens.splice(i + 1, 3); // drop the title-only paragraph
      if (tokens[i + 1]?.type === 'callout_close') {
        const p = new state.Token('paragraph_open', 'p', 1);
        const empty = new state.Token('inline', '', 0);
        empty.content = '';
        empty.children = [];
        tokens.splice(i + 1, 0, p, empty, new state.Token('paragraph_close', 'p', -1));
      }
    } else {
      inline.content = inline.content.slice(nl + 1);
    }
  }
}

/** Table cells hold inline content in markdown-it but paragraphs in the editor. */
function tableCellsRule(state: StateCore) {
  const tokens = state.tokens;
  for (let i = 0; i < tokens.length; i++) {
    const t = tokens[i]!;
    if ((t.type === 'th_open' || t.type === 'td_open') && tokens[i + 1]?.type === 'inline') {
      tokens.splice(
        i + 1,
        1,
        new state.Token('paragraph_open', 'p', 1),
        tokens[i + 1]!,
        new state.Token('paragraph_close', 'p', -1),
      );
    }
  }
}

/** A paragraph holding only `![[x]]` becomes a block embed; other embeds stay raw text. */
function embedsRule(state: StateCore) {
  const tokens = state.tokens;
  for (let i = 0; i < tokens.length; i++) {
    const inline = tokens[i]!;
    if (inline.type !== 'inline' || !inline.children) continue;
    const meaningful = inline.children.filter((c) => !(c.type === 'text' && !c.content.trim()));
    const isBlock =
      tokens[i - 1]?.type === 'paragraph_open' &&
      meaningful.length === 1 &&
      meaningful[0]!.type === 'embed_inline' &&
      tokens[i - 2]?.type !== 'th_open' &&
      tokens[i - 2]?.type !== 'td_open';
    if (isBlock) {
      const embed = new state.Token('embed', '', 0);
      embed.meta = meaningful[0]!.meta;
      tokens.splice(i - 1, 3, embed);
      continue;
    }
    for (const c of inline.children) {
      if (c.type === 'embed_inline') {
        c.type = 'raw_inline';
        c.meta = null;
      }
    }
  }
}

/**
 * The HTML Nexus writes for formatting Markdown has no syntax for becomes marks
 * again: `<u>`, `<span style="color: …">`, `<mark style="background: …">`.
 * Only matched open/close pairs are converted; anything else stays raw HTML.
 */
const HTML_MARKS: { kind: string; open: RegExp; close: RegExp }[] = [
  { kind: 'underline', open: /^<u>$/i, close: /^<\/u>$/i },
  { kind: 'color', open: /^<span style="color:\s*([^";]+?);?\s*">$/i, close: /^<\/span>$/i },
  {
    kind: 'hcolor',
    open: /^<mark style="background(?:-color)?:\s*([^";]+?);?\s*">$/i,
    close: /^<\/mark>$/i,
  },
];

function htmlMarksRule(state: StateCore) {
  for (const block of state.tokens) {
    if (block.type !== 'inline' || !block.children) continue;
    const stack: { kind: string; token: Token; color: string | null }[] = [];
    for (const tok of block.children) {
      if (tok.type !== 'html_inline') continue;
      const content = tok.content.trim();
      const open = HTML_MARKS.find((m) => m.open.test(content));
      if (open) {
        stack.push({ kind: open.kind, token: tok, color: open.open.exec(content)![1] ?? null });
        continue;
      }
      const close = HTML_MARKS.find((m) => m.close.test(content));
      const top = stack[stack.length - 1];
      if (!close || top?.kind !== close.kind) continue;
      stack.pop();
      top.token.type = `${top.kind}_open`;
      top.token.meta = { color: top.color };
      top.token.nesting = 1;
      tok.type = `${top.kind}_close`;
      tok.nesting = -1;
    }
  }
}

/** `<p align="center">…</p>` (and headings): an aligned paragraph with Markdown inside. */
const ALIGNED_RE = /^<(p|h[1-6]) align="(left|center|right|justify)">([\s\S]*?)<\/\1>\s*$/i;

function alignedBlocksRule(state: StateCore) {
  const tokens = state.tokens;
  for (let i = 0; i < tokens.length; i++) {
    const t = tokens[i]!;
    const m = t.type === 'html_block' ? ALIGNED_RE.exec(t.content) : null;
    if (!m) continue;
    const tag = m[1]!.toLowerCase();
    const type = tag === 'p' ? 'paragraph' : 'heading';
    const open = new state.Token(`${type}_open`, tag, 1);
    open.meta = { align: m[2]!.toLowerCase() };
    open.map = t.map;
    open.level = t.level;
    const inline = new state.Token('inline', '', 0);
    inline.content = m[3]!.trim();
    inline.map = t.map;
    inline.level = t.level + 1;
    inline.children = [];
    const close = new state.Token(`${type}_close`, tag, -1);
    close.level = t.level;
    tokens.splice(i, 1, open, inline, close);
    i += 2;
  }
}

const COLUMN_MARKERS: Record<string, 'start' | 'next' | 'end'> = {
  '<!-- columns -->': 'start',
  '<!-- column -->': 'next',
  '<!-- /columns -->': 'end',
};

/** `<!-- columns -->` … `<!-- column -->` … `<!-- /columns -->` become columns. */
function columnsRule(state: StateCore) {
  const tokens = state.tokens;
  const marker = (t: Token) =>
    t.type === 'html_block' ? COLUMN_MARKERS[t.content.trim()] : undefined;
  for (let i = 0; i < tokens.length; i++) {
    if (marker(tokens[i]!) !== 'start') continue;
    const level = tokens[i]!.level;
    // The matching end at the same level, with only "next" markers in between.
    const parts = [i];
    let end = -1;
    for (let j = i + 1; j < tokens.length; j++) {
      const t = tokens[j]!;
      if (t.level < level) break;
      const k = t.level === level ? marker(t) : undefined;
      if (k === 'start') break;
      if (k === 'next') parts.push(j);
      if (k === 'end') {
        end = j;
        break;
      }
    }
    if (end < 0 || parts.length < 2 || parts.length > 4) continue;
    const tok = (type: string, nesting: 1 | -1) => {
      const x = new state.Token(type, 'div', nesting);
      x.level = level;
      return x;
    };
    const emptyParagraph = () => {
      const inline = new state.Token('inline', '', 0);
      inline.children = [];
      return [tok('paragraph_open', 1), inline, tok('paragraph_close', -1)];
    };
    const out: Token[] = [tok('columns_open', 1)];
    const bounds = [...parts, end];
    for (let n = 0; n < bounds.length - 1; n++) {
      const body = tokens.slice(bounds[n]! + 1, bounds[n + 1]);
      out.push(tok('column_open', 1), ...(body.length ? body : emptyParagraph()));
      out.push(tok('column_close', -1));
    }
    out.push(tok('columns_close', -1));
    tokens.splice(i, end - i + 1, ...out);
    i += out.length - 1;
  }
}

export function createTokenizer() {
  const md = new MarkdownIt('commonmark', { html: true, linkify: false });
  md.enable(['table', 'strikethrough']);
  md.use(markPlugin);
  md.inline.ruler.before('link', 'wikilink', wikilinkRule);
  md.inline.ruler.before('emphasis', 'hashtag', hashtagRule);
  md.inline.ruler.before('emphasis', 'inline_comment', inlineCommentRule);
  md.block.ruler.before('paragraph', 'block_comment', blockCommentRule);
  md.block.ruler.before('reference', 'footnote_def', footnoteDefRule, {
    alt: ['paragraph', 'reference'],
  });
  md.core.ruler.after('block', 'nexus_tasks', taskListsRule);
  md.core.ruler.after('nexus_tasks', 'nexus_callouts', calloutsRule);
  md.core.ruler.after('nexus_callouts', 'nexus_table_cells', tableCellsRule);
  md.core.ruler.after('nexus_table_cells', 'nexus_aligned', alignedBlocksRule);
  md.core.ruler.after('nexus_aligned', 'nexus_columns', columnsRule);
  md.core.ruler.after('inline', 'nexus_embeds', embedsRule);
  md.core.ruler.after('nexus_embeds', 'nexus_html_marks', htmlMarksRule);
  return md;
}
